/**
 * «Quanto»: serie e colpi di un esercizio di tiro, in un riquadro solo.
 *
 * Prima c'erano due posti: «Serie» (un numero) in alto e «Serie previste»
 * (le righe con la distanza) più in basso, e il coach non capiva quale dei
 * due servisse. Servono insieme — il numero dice *quante*, la riga dice *cosa
 * cambia* in quella serie — quindi stanno insieme: il numero governa le
 * righe, e le righe si aprono solo se c'è qualcosa da dire (la distanza che
 * cambia, il lento mirato a 5/10/15 m). Su uno stage con il box di partenza
 * non c'è niente da dire, e le righe restano chiuse.
 *
 * I **colpi sono per serie**, e il riquadro lo scrive: «colpi totale non è
 * chiaro» nasceva dal fatto che il form parlava di colpi previsti e il
 * designer di colpi della disposizione, che è una serie sola. Il totale —
 * le munizioni da portare — si deriva e si mostra.
 *
 * Lo scarto fra colpi dichiarati e bersagli si vede qui e si chiude con un
 * gesto: è il caso della sagoma sola con «5 colpi», su cui il tiratore
 * trovava la scheda ferma a 2.
 *
 * Usato dal ProgramBuilder e dalla libreria drill: una superficie sola.
 * Spec: docs/shooting/15-seconda-sessione-federico.md
 */
import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Field, Section } from "./formPrimitives";
import { requiredShots } from "./stageElements";
import {
	applyRoundsToTargets,
	plannedSeriesCount,
	plannedSeriesList,
	resizePlannedSeries,
	roundsMismatch,
	roundsPerSeries,
	seriesPlanHasDetail,
	totalRoundsPlanned,
} from "./shootingPlan";

/** `{declared}` → 5. Le frasi stanno in LanguageContext, i numeri qui. */
const fill = (template, vars) =>
	Object.entries(vars).reduce(
		(text, [key, value]) => text.split(`{${key}}`).join(String(value)),
		String(template || ""),
	);

const inputClass = "bg-background border-transparent rounded-sm h-11";

export function SeriesPlanSection({ shooting = {}, onChange, t }) {
	const list = plannedSeriesList(shooting);
	const count = plannedSeriesCount(shooting);
	const targets = Array.isArray(shooting.targets) ? shooting.targets : [];
	const fromTargets = requiredShots(targets);
	const perSeries = roundsPerSeries(shooting);
	const total = totalRoundsPlanned(shooting);
	const mismatch = roundsMismatch(shooting);
	const applied = mismatch ? applyRoundsToTargets(targets, mismatch.declared) : null;
	const hasDetail = list.some(seriesPlanHasDetail);
	const [detailOpen, setDetailOpen] = useState(hasDetail);

	// Un drill applicato dalla libreria porta le sue righe: si aprono da sole.
	useEffect(() => {
		if (hasDetail) setDetailOpen(true);
	}, [hasDetail]);

	const setCount = (raw) => {
		const parsed = raw === "" ? 0 : parseInt(raw, 10);
		const next = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
		onChange({
			series_planned: next || null,
			series: resizePlannedSeries(list, next),
		});
	};

	const updateRow = (index, changes) =>
		onChange({
			series: list.map((row, i) => (i === index ? { ...row, ...changes } : row)),
		});

	const removeRow = (index) => {
		const next = list.filter((_, i) => i !== index);
		onChange({ series: next, series_planned: next.length || null });
	};

	return (
		<Section title={t("pbShootHowMuch")} hint={t("pbShootHowMuchHintLive")}>
			<div className="grid grid-cols-2 gap-2">
				<Field label={t("pbShootSeriesCount")} hint={t("pbShootSeriesCountHint")}>
					<Input
						type="number"
						min="0"
						inputMode="numeric"
						value={count || ""}
						onChange={(e) => setCount(e.target.value)}
						className={inputClass}
						placeholder="4"
					/>
				</Field>
				<Field
					label={t("pbShootRoundsPerSeries")}
					hint={
						fromTargets
							? `${fromTargets} ${t("pbShootRoundsFromTargets")}`
							: t("pbShootRoundsPerSeriesHint")
					}
				>
					<Input
						type="number"
						min="0"
						inputMode="numeric"
						value={shooting.rounds_planned ?? ""}
						onChange={(e) =>
							onChange({
								rounds_planned: e.target.value
									? parseInt(e.target.value, 10)
									: null,
							})
						}
						className={inputClass}
						placeholder={fromTargets ? String(fromTargets) : "12"}
					/>
				</Field>
			</div>

			{/* Munizioni da portare: è il numero che il coach aveva in testa
			    quando leggeva «colpi previsti» come totale. */}
			{count > 1 && perSeries > 0 && (
				<p className="text-[11px] text-muted-foreground">
					{t("pbShootRoundsTotal")}: {perSeries} × {count} ={" "}
					<span className="text-foreground font-medium">{total}</span>
				</p>
			)}

			{/* Avviso, non blocco: ma con il gesto che lo chiude. */}
			{mismatch && (
				<div className="rounded-sm border border-amber-400/40 bg-amber-400/10 px-2 py-1.5 space-y-1.5">
					<p className="text-[11px] text-amber-400 leading-snug">
						{fill(t("pbShootRoundsMismatch"), mismatch)}
					</p>
					{applied ? (
						<Button
							type="button"
							size="sm"
							variant="outline"
							className="rounded-sm h-7 text-xs"
							onClick={() => {
								onChange({ targets: applied });
								toast.success(t("pbShootRoundsApplied"));
							}}
						>
							{fill(t("pbShootRoundsApply"), mismatch)}
						</Button>
					) : (
						<p className="text-[11px] text-muted-foreground">
							{t("pbShootRoundsApplyManual")}
						</p>
					)}
				</div>
			)}

			{count > 0 && (
				<div className="space-y-1.5">
					<button
						type="button"
						onClick={() => setDetailOpen((open) => !open)}
						className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
					>
						{detailOpen ? (
							<ChevronDown className="w-3.5 h-3.5" />
						) : (
							<ChevronRight className="w-3.5 h-3.5" />
						)}
						{t("pbShootSeriesDetail")}
						{hasDetail && !detailOpen && (
							<span className="text-volt">·</span>
						)}
					</button>
					{detailOpen && (
						<>
							<p className="text-[10px] text-muted-foreground leading-snug">
								{t("pbShootSeriesDetailHint")}
							</p>
							<div className="grid grid-cols-[2rem,5.5rem,1fr,2rem] gap-1.5 px-1">
								{["#", t("pbShootSeriesDistance"), t("pbShootSeriesLabel"), ""].map(
									(head, i) => (
										<span
											key={`${head}_${i}`}
											className="text-[10px] uppercase tracking-wider text-muted-foreground"
										>
											{head}
										</span>
									),
								)}
							</div>
							{list.map((row, index) => (
								<div
									key={`serie_${index}`}
									className="grid grid-cols-[2rem,5.5rem,1fr,2rem] gap-1.5 items-center"
								>
									<span className="text-xs text-muted-foreground text-center">
										{index + 1}
									</span>
									<Input
										type="number"
										step="0.5"
										inputMode="decimal"
										value={row.distance_m ?? ""}
										onChange={(e) =>
											updateRow(index, {
												distance_m: e.target.value
													? parseFloat(e.target.value)
													: null,
											})
										}
										className="bg-background border-transparent rounded-sm text-xs px-2 h-10"
										placeholder="m"
									/>
									<Input
										value={row.label || ""}
										onChange={(e) => updateRow(index, { label: e.target.value })}
										className="bg-background border-transparent rounded-sm text-xs px-2 h-10"
										placeholder={t("pbShootSeriesLabelPlaceholder")}
									/>
									<Button
										type="button"
										variant="ghost"
										size="icon"
										className="rounded-sm hover:text-red-500 h-8 w-8"
										onClick={() => removeRow(index)}
									>
										<Trash2 className="w-3.5 h-3.5" />
									</Button>
								</div>
							))}
						</>
					)}
				</div>
			)}
		</Section>
	);
}

/**
 * «Quanto» del dry fire: ripetizioni e par time. Non ha serie né colpi, e
 * un riquadro con quei campi lo confonderebbe con il live fire.
 */
export function DryFireQuantityFields({ reps, onRepsChange, parTime, onParTimeChange, t }) {
	return (
		<Section title={t("pbShootHowMuch")} hint={t("pbShootDryQuantityHint")}>
			<div className="grid grid-cols-2 gap-2">
				<Field label={t("pbShootReps")}>
					<Input
						type="number"
						min="0"
						inputMode="numeric"
						value={reps ?? ""}
						onChange={(e) => onRepsChange(e.target.value)}
						className={inputClass}
						placeholder="20"
					/>
				</Field>
				<Field label={t("pbShootParTime")}>
					<Input
						type="number"
						step="0.01"
						inputMode="decimal"
						value={parTime ?? ""}
						onChange={(e) =>
							onParTimeChange(e.target.value ? parseFloat(e.target.value) : null)
						}
						className={inputClass}
						placeholder="1.50"
					/>
				</Field>
			</div>
		</Section>
	);
}
